import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import {
  IncentiveOffer,
  IncentiveOfferType,
} from 'modules/incentivesMarket/types';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';
import { SellSelection } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';
import { mapSellerPlansToSellPlans } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/mapSellerPlansToSellPlans';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import { ButtonVariants } from 'modules/_shared/components/Buttons';

import IncentiveOfferSellUnitsPanel from './IncentiveOfferSellUnitsPanel';

type IncentiveOfferActionCardProps = {
  offer: IncentiveOffer;
  availableShares: number;
  sellerPlans?: IncentivesMarketPlanContext[];
  isExpired: boolean;
  isOwnOffer: boolean;
  hasPartnerBuyer: boolean;
  isAdmin: boolean;
  isBeneficiaryView: boolean;
  hasParticipatedInOffer?: boolean;
  /* eslint-disable-next-line no-unused-vars */
  onSellUnitsConfirm: (selection: SellSelection) => void;
  onViewOtherOffers: () => void;
};

const IncentiveOfferActionCard: FC<IncentiveOfferActionCardProps> = ({
  offer,
  availableShares,
  sellerPlans = [],
  isExpired,
  isOwnOffer,
  hasPartnerBuyer,
  isAdmin,
  isBeneficiaryView,
  hasParticipatedInOffer = false,
  onSellUnitsConfirm,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === IncentiveOfferType.SELL;
  const isAdminOnlyView = isAdmin && !isBeneficiaryView;
  const isSellerAcceptanceFlow =
    !isSellOffer &&
    !isAdminOnlyView &&
    !isOwnOffer &&
    (isBeneficiaryView || hasPartnerBuyer);

  const mustSellAll = !offer.isPartialAmountEnabled;

  const minPurchaseQuantity = useMemo(() => {
    if (mustSellAll) return availableShares;
    return Math.max(
      1,
      Math.min(offer.minUnitsPerAcceptance ?? 1, availableShares)
    );
  }, [availableShares, mustSellAll, offer.minUnitsPerAcceptance]);

  const sellPlans = useMemo(
    () =>
      isSellerAcceptanceFlow
        ? mapSellerPlansToSellPlans({ offer, sellerPlans })
        : [],
    [isSellerAcceptanceFlow, offer, sellerPlans]
  );

  if (!isExpired && isAdminOnlyView && !isSellOffer) {
    return null;
  }

  if (isExpired) {
    return (
      <div
        className="card card-bordered"
        style={{ position: 'sticky', top: '20px' }}
      >
        <div className="card-inner text-center py-4">
          <p className="text-soft mb-3">{t('ExpiredOfferActionDescription')}</p>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onViewOtherOffers}
          >
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    );
  }

  if (!isSellerAcceptanceFlow) {
    return null;
  }

  if (hasParticipatedInOffer) {
    return null;
  }

  if (availableShares <= 0) {
    return (
      <EmptyStateCard
        mainMessage={t('IncentivesMarketNoUnitsToSellTitle')}
        infoMessage={t('IncentivesMarketNoUnitsToSellDescription')}
        centered
        action={{
          label: t('ViewOtherOffers'),
          onClick: onViewOtherOffers,
          variant: ButtonVariants.LIGHT,
          icon: 'arrow-left',
        }}
      />
    );
  }

  return (
    <IncentiveOfferSellUnitsPanel
      plans={sellPlans}
      isDisabled={isOwnOffer || !hasPartnerBuyer}
      mustSellAll={mustSellAll}
      minUnits={minPurchaseQuantity}
      maxTotalUnits={availableShares}
      onConfirm={onSellUnitsConfirm}
      onViewOtherOffers={onViewOtherOffers}
    />
  );
};

export default IncentiveOfferActionCard;
